import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { com, Nullable } from '@eidu/form'
import { Box, Button, Stack, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import DraftFieldValue, {
  draftFieldValuesToFieldValues,
  fieldValueToDraftFieldValue,
  fieldValueToString,
} from '../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../util/EqualityHashMap'
import { requireNotUndefinedOrNull } from '../../util/require'
import createEntityToCreate from './createEntityToCreate'
import hasFieldError from '../../domain/entity/hasFieldError'
import { logException } from '../../util/Logging'
import useEntityRepository from '../../io/useEntityRepository'
import usePermittedActions from '../../io/usePermittedActions'
import PermissionError from './PermissionError'
import { ConflictError } from '../../api/entity/data/postEntities'
import getEntitiesBatch from '../../api/entity/data/getEntitiesBatch'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import EntityFormInput from '../../components/input/EntityFormInput'
import LoginIdentifierField from '../../components/input/LoginIdentifierField'
import ConfirmationDialog from '../../components/feedback/ConfirmationDialog'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import AssetPage from '../assets/AssetPage'
import { findAssetTypeByLabel, type RenderData } from '../assets/assetTypes'
import { useAssetTypeRegistry } from '../assets/useAssetTypeRegistry'
import { getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import { isManualOrderField } from '../../util/entityType/orderField'
import { useAssistantContext, type AssistantEditorValueUpdate } from '../../components/assistant/AssistantContext'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import apiBaseUrl from '../../api/baseUrl'
import entityApiUrl from '../../api/entity/baseUrl'
import getEntitiesFromResponses from '../../domain/entity/getEntitiesFromResponses'
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import Entity = com.eidu.sharedlib.entity.Entity
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import PermittedEntityAction = com.eidu.sharedlib.entity.permission.PermittedEntityAction
import emailLoginIdentifierKey = com.eidu.sharedlib.entity.emailLoginIdentifierKey
import phoneNumberLoginIdentifierKey = com.eidu.sharedlib.entity.phoneNumberLoginIdentifierKey
import PhoneNumber = com.eidu.sharedlib.user.PhoneNumber
import isRealMobilePhoneNumber = com.eidu.sharedlib.user.isRealMobilePhoneNumber
import ExternalValueKey = com.eidu.sharedlib.entity.user.ExternalValueKey
import linkKeyFromString = com.eidu.sharedlib.entity.link.linkKeyFromString
import Link from '../../domain/entity/Link'
import LearningUnitEditorWrapper from '../../ui/microfrontend/LearningUnitEditorWrapper'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityTypeNotAccessibleError from './EntityTypeNotAccessibleError'

// Validation utility functions
const isValidEmailOrEmpty = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return email === '' || emailRegex.test(email)
}

const isValidPhoneNumberOrEmpty = (phone: string): boolean => {
  const phoneNumber = PhoneNumber.fromString(phone)
  return phone === '' || !!(phoneNumber && isRealMobilePhoneNumber(phoneNumber))
}

const getNonNullFieldsFromEntity = (entity: Entity): ReadonlyMap<FieldId, DraftFieldValue> =>
  new EqualityHashMap<FieldId, DraftFieldValue>(
    Array.from(entity.valuesByFieldId.asJsReadonlyMapView())
      .filter((it): it is [FieldId, FieldValue] => it[1] !== null)
      .map(([id, value]) => [id, fieldValueToDraftFieldValue(id, value)])
  )

const getFieldById = (entityType: EntityType, fieldId?: FieldId) =>
  fieldId ? entityType.fields.asJsReadonlyArrayView().find((field) => field.id.equals(fieldId)) : undefined

const resolveFieldTypeName = (fieldType: FieldType) => {
  if (typeof fieldType === 'string') return fieldType
  if (typeof fieldType === 'number') {
    return (FieldType as unknown as Record<number, string>)[fieldType] ?? String(fieldType)
  }
  return String(fieldType)
}

const ENTITY_SYNC_INTERVAL_MS = 15000

type CreatePrefillState = {
  hierarchyCreatePrefill?: Array<{
    fieldId: string
    value: string
  }>
}

const buildSnapshotFromEntity = (entityType: EntityType, entity: Entity, phone: string, email: string): string => {
  const values = entity.valuesByFieldId.asJsReadonlyMapView()
  const fields = entityType.fields
    .asJsReadonlyArrayView()
    .map((field) => [field.id.toString(), fieldValueToString(values.get(field.id) ?? null)] as const)
  return JSON.stringify({ fields, phone: phone ?? '', email: email ?? '' })
}

const buildSnapshotFromFields = (
  entityType: EntityType,
  fieldsMap: ReadonlyMap<FieldId, DraftFieldValue>,
  phone: string,
  email: string
): string => {
  const fields = entityType.fields
    .asJsReadonlyArrayView()
    .map((field) => [field.id.toString(), fieldsMap.get(field.id)?.value ?? ''] as const)
  return JSON.stringify({ fields, phone: phone ?? '', email: email ?? '' })
}

const getExternalValue = (entity: Entity, key: ExternalValueKey): string => {
  const externalValues = entity.externalValues?.asJsReadonlyMapView()
  return externalValues?.get(key) || ''
}

export type ReadyCreateOrEditPageProps = {
  entityId?: EntityId
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const ReadyCreateOrEditPage = ({ entityId, entityType, entityTypes }: ReadyCreateOrEditPageProps) => {
  const entityRepository = useEntityRepository(
    {
      typeId: entityType.id,
      types: entityTypes,
    },
    [entityType.id.toString(), entityTypes.size]
  )

  const requiredEntityActions: EntityAction[] = [EntityAction.ReadEntity, EntityAction.UpdateEntity]

  const requested = useRef([
    new PermittedGlobalAction(GlobalAction.ManageAuthentication),
    ...(entityId ? requiredEntityActions.map((entityAction) => new PermittedEntityAction(entityAction, entityId)) : []),
  ])
  const [permissions] = usePermittedActions(requested.current)

  const missingEntityActions = entityId
    ? requiredEntityActions.filter(
        (action) =>
          !permissions?.some(
            (permittedAction) =>
              permittedAction instanceof PermittedEntityAction &&
              permittedAction.target.equals(entityId) &&
              permittedAction.action === action
          )
      )
    : []

  const hasRequiredEntityActions = missingEntityActions.length === 0

  // Important: entityId must not change between undefined and
  // not undefined, otherwise React's hook order will be messed up.
  const entity = entityId !== undefined ? entityRepository.useItem(entityId) : undefined

  const navigate = useNavigate()
  const location = useLocation()
  const { showSnackBar } = useSnackBar()

  const { assetEntityTypesById } = useAssetTypeRegistry(entityTypes)
  const assetEntityConfig = useMemo(
    () => (entityType ? assetEntityTypesById.get(entityType.id) : undefined),
    [assetEntityTypesById, entityType]
  )
  const payloadField = useMemo(
    () => (entityType && assetEntityConfig ? getFieldById(entityType, assetEntityConfig.payloadFieldId) : undefined),
    [assetEntityConfig, entityType]
  )

  const [assetPayload, setAssetPayload] = useState('')
  const [assistantUpdateToken, setAssistantUpdateToken] = useState<number | undefined>(undefined)
  const baselineSnapshotRef = useRef<string | null>(null)
  const syncInFlightRef = useRef(false)

  // Important: all use* need to run, so don't replace this by anything using short-circuiting!
  const error = [entityRepository.useError(), entity === null ? 'Entity not found' : undefined]
    .find((it) => !!it)
    ?.let((it) => String(it))
  const loading = [entityRepository.useLoading(), entityId !== undefined && entity === undefined].some((it) => it)
  const [submitting, setSubmitting] = useState(false)

  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new Map())
  const updateField = useCallback(
    (value: DraftFieldValue) => setFields((it) => new EqualityHashMap(it).set(value.fieldId, value)),
    []
  )

  const activeAssetType = useMemo(() => {
    if (!assetEntityConfig) return undefined
    if (assetEntityConfig.allowedTypes.length === 1) return assetEntityConfig.allowedTypes[0]
    if (!assetEntityConfig.typeFieldId) return undefined
    const typeValue = fields.get(assetEntityConfig.typeFieldId)?.value?.trim()
    if (!typeValue) return undefined
    const resolvedType = findAssetTypeByLabel(typeValue)
    if (!resolvedType) return undefined
    return assetEntityConfig.allowedTypeIds.includes(resolvedType.id) ? resolvedType : undefined
  }, [assetEntityConfig, fields])

  const isRenderableAssetType = Boolean(activeAssetType && activeAssetType.payloadFormat === 'text' && payloadField)

  const [phone, setPhone] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [phoneError, setPhoneError] = useState<string | undefined>()
  const [emailError, setEmailError] = useState<string | undefined>()
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const { setCurrentView, setEditorAdapter } = useAssistantContext()
  const iAuthContext = useContext<IAuthContext>(AuthContext)
  const previewAuth = useMemo(
    () => createAuthContext(iAuthContext),
    [iAuthContext.token, iAuthContext.idTokenData?.organization_id, iAuthContext.idTokenData?.user_entity_id]
  )
  const prefillByFieldId = useMemo(() => {
    if (entityId) return new Map<string, string>()
    const state = location.state as CreatePrefillState | null
    const prefill = Array.isArray(state?.hierarchyCreatePrefill) ? state.hierarchyCreatePrefill : []
    return new Map(
      prefill
        .filter(
          (entry): entry is { fieldId: string; value: string } =>
            typeof entry?.fieldId === 'string' && typeof entry.value === 'string'
        )
        .map((entry) => [entry.fieldId, entry.value])
    )
  }, [entityId, location.state])

  useEffect(() => {
    if (entity) {
      setFields(getNonNullFieldsFromEntity(entity.entity))
      const nextPhone = getExternalValue(entity.entity, phoneNumberLoginIdentifierKey)
      const nextEmail = getExternalValue(entity.entity, emailLoginIdentifierKey)
      setPhone(nextPhone)
      setEmail(nextEmail)

      baselineSnapshotRef.current = buildSnapshotFromEntity(entityType, entity.entity, nextPhone, nextEmail)

      if (payloadField) {
        const existingContent = entity.entity.valuesByFieldId.asJsReadonlyMapView().get(payloadField.id)
        setAssetPayload(existingContent ? fieldValueToString(existingContent as FieldValue) : '')
      }
    } else {
      const emptyFields = new EqualityHashMap<FieldId, DraftFieldValue>()
      if (payloadField) {
        const payloadPrefill = prefillByFieldId.get(payloadField.id.toString())
        emptyFields.set(payloadField.id, {
          fieldId: payloadField.id,
          type: payloadField.type,
          value: payloadPrefill ?? '',
        })
      }
      entityType.fields.asJsReadonlyArrayView().forEach((field) => {
        if (payloadField && payloadField.id.equals(field.id)) return
        const prefillValue = prefillByFieldId.get(field.id.toString())
        if (prefillValue === undefined) return
        emptyFields.set(field.id, {
          fieldId: field.id,
          type: field.type,
          value: prefillValue,
        })
      })
      setFields(emptyFields)
      setPhone('')
      setEmail('')
      setAssetPayload(payloadField ? prefillByFieldId.get(payloadField.id.toString()) ?? '' : '')
      baselineSnapshotRef.current = null
    }
    setAssistantUpdateToken((prev) => (prev ?? 0) + 1)
  }, [emailLoginIdentifierKey, entity, entityType, payloadField, phoneNumberLoginIdentifierKey, prefillByFieldId])

  const editorInstructions = useMemo(() => {
    const prompt = activeAssetType?.systemPrompt?.trim()
    return prompt && prompt.length > 0 ? prompt : undefined
  }, [activeAssetType?.systemPrompt])

  const editorFieldsSnapshot = useMemo(
    () =>
      entityType.fields.asJsReadonlyArrayView().map((field) => ({
        fieldId: field.id.toString(),
        name: field.name,
        type: resolveFieldTypeName(field.type),
        value: fields.get(field.id)?.value ?? '',
        required: field.required,
      })),
    [entityType, fields]
  )
  const renderFields = useMemo(
    () => editorFieldsSnapshot.map(({ fieldId, ...rest }) => ({ id: fieldId, ...rest })),
    [editorFieldsSnapshot]
  )

  const renderData = useMemo<RenderData | undefined>(() => {
    if (!entityType) return undefined
    const entitySnapshot = {
      id: entityId?.toString(),
      typeId: entityType.id.toString(),
      typeName: getTypeDisplayName(entityType.name),
      fields: renderFields,
    }
    const auth =
      previewAuth &&
      ({
        accessToken: previewAuth.accessToken,
        organizationId: previewAuth.organizationId,
        apiBaseUrl,
        entityApiUrl,
        userEntityId: previewAuth.userEntityId,
      } as const)
    return { entity: entitySnapshot, auth: auth ?? undefined, context: { embedDepth: 0 } }
  }, [entityId, entityType, previewAuth, renderFields])

  const buildLocalSnapshot = useCallback(
    () => buildSnapshotFromFields(entityType, fields, phone, email),
    [entityType, fields, phone, email]
  )

  const fetchLatestEntity = useCallback(async () => {
    if (!entityId) return null
    if (!previewAuth?.accessToken || !previewAuth.organizationId) return null
    const response = await getEntitiesBatch({
      ids: [entityId],
      withRelatedEntities: true,
      pageIndex: 0,
      pageSize: 1,
      authContext: previewAuth,
    })
    const entities = getEntitiesFromResponses([response], entityTypes)
    return entities.get(entityId) ?? null
  }, [entityId, entityTypes, previewAuth])

  useEffect(() => {
    if (!entityId || !entity || !isRenderableAssetType) return undefined

    let cancelled = false
    const interval = window.setInterval(async () => {
      if (cancelled || syncInFlightRef.current) return
      if (document.visibilityState === 'hidden') return

      const baselineSnapshot = baselineSnapshotRef.current
      if (!baselineSnapshot) return

      const localSnapshot = buildLocalSnapshot()
      if (localSnapshot !== baselineSnapshot) return

      syncInFlightRef.current = true
      try {
        const latestEntity = await fetchLatestEntity()
        if (cancelled || !latestEntity) return

        const latestPhone = getExternalValue(latestEntity.entity, phoneNumberLoginIdentifierKey)
        const latestEmail = getExternalValue(latestEntity.entity, emailLoginIdentifierKey)
        const latestSnapshot = buildSnapshotFromEntity(entityType, latestEntity.entity, latestPhone, latestEmail)

        if (baselineSnapshotRef.current !== baselineSnapshot) return
        if (buildLocalSnapshot() !== baselineSnapshot) return
        if (latestSnapshot === baselineSnapshot) return

        await entityRepository.setEntity(latestEntity)
      } catch (error) {
        logException(error)
      } finally {
        syncInFlightRef.current = false
      }
    }, ENTITY_SYNC_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [
    buildLocalSnapshot,
    emailLoginIdentifierKey,
    entity,
    entityId,
    entityRepository,
    entityType,
    fetchLatestEntity,
    isRenderableAssetType,
    phoneNumberLoginIdentifierKey,
  ])

  const assetTypeInfo = useMemo(
    () =>
      activeAssetType
        ? {
            id: activeAssetType.id,
            name: activeAssetType.displayName,
            payloadFormat: activeAssetType.payloadFormat,
            contentLanguage: activeAssetType.contentLanguage,
          }
        : undefined,
    [activeAssetType]
  )

  useEffect(() => {
    setCurrentView(
      assetEntityConfig
        ? {
            kind: 'asset-editor',
            entityId: entityId?.toString(),
            typeId: entityType.id.toString(),
            typeName: getTypeDisplayName(entityType.name),
            assetType: assetTypeInfo,
            fields: editorFieldsSnapshot,
            editorInstructions,
          }
        : {
            kind: 'entity-editor',
            entityId: entityId?.toString(),
            typeId: entityType.id.toString(),
            typeName: getTypeDisplayName(entityType.name),
            fields: editorFieldsSnapshot,
          }
    )
    return () => {
      setCurrentView(undefined)
    }
  }, [assetEntityConfig, assetTypeInfo, editorFieldsSnapshot, editorInstructions, entityId, entityType, setCurrentView])

  useEffect(() => {
    if (!isValidEmailOrEmpty(email) && hasAttemptedSubmit) {
      setEmailError('Invalid email format')
    } else {
      setEmailError(undefined)
    }

    if (!isValidPhoneNumberOrEmpty(phone) && hasAttemptedSubmit) {
      setPhoneError('Invalid phone number format')
    } else {
      setPhoneError(undefined)
    }
  }, [email, phone, hasAttemptedSubmit])

  const handleAssetPayloadChange = useCallback(
    (value: string, _options?: { fromAssistant?: boolean }) => {
      if (!payloadField) return
      setAssetPayload(value)
      const draftValue: DraftFieldValue = {
        fieldId: payloadField.id,
        type: payloadField.type,
        value,
      }
      setFields((previous) => new EqualityHashMap(previous).set(payloadField.id, draftValue))
    },
    [activeAssetType?.id, payloadField]
  )

  const editorFieldById = useMemo(
    () => new Map(entityType.fields.asJsReadonlyArrayView().map((field) => [field.id.toString(), field] as const)),
    [entityType]
  )

  const editorFieldByName = useMemo(() => {
    const normalize = (value: string) => value.trim().toLowerCase()
    return new Map(entityType.fields.asJsReadonlyArrayView().map((field) => [normalize(field.name), field] as const))
  }, [entityType])

  const payloadFieldId = payloadField?.id.toString()

  const applyEditorValues = useCallback(
    (updates: AssistantEditorValueUpdate[]) => {
      const toDraftValue = (value: unknown) => {
        if (value === null || value === undefined) return ''
        if (typeof value === 'string') return value
        if (typeof value === 'number' || typeof value === 'boolean') return String(value)
        try {
          return JSON.stringify(value)
        } catch {
          return String(value)
        }
      }

      const errors: string[] = []
      let applied = 0

      updates.forEach((update, index) => {
        const field =
          (update.fieldId && editorFieldById.get(update.fieldId)) ||
          (update.fieldName && editorFieldByName.get(update.fieldName.trim().toLowerCase()))
        if (!field) {
          errors.push(`Unknown field at index ${index + 1}`)
          return
        }
        const nextValue = toDraftValue(update.value)
        if (payloadFieldId && field.id.toString() === payloadFieldId) {
          handleAssetPayloadChange(nextValue, { fromAssistant: true })
        } else {
          updateField({ fieldId: field.id, type: field.type, value: nextValue })
        }
        applied += 1
      })

      if (applied > 0) {
        setAssistantUpdateToken((prev) => (prev ?? 0) + 1)
      }
      return { applied, errors }
    },
    [editorFieldById, editorFieldByName, handleAssetPayloadChange, payloadFieldId, setAssistantUpdateToken, updateField]
  )

  useEffect(() => {
    setEditorAdapter({ applyEditorValues })
    return () => {
      setEditorAdapter(undefined)
    }
  }, [applyEditorValues, setEditorAdapter])

  const valid = useMemo(() => {
    if (!entityType) return false

    const fieldsValid = entityType.fields.asJsReadonlyArrayView().every((field) => {
      const fieldValue = fields.get(field.id) ?? undefined
      return !hasFieldError(field, fieldValue, new Map())
    })

    const loginIdentifiersValid = !hasAttemptedSubmit || (!phoneError && !emailError)

    return fieldsValid && loginIdentifiersValid
  }, [entityType, fields, hasAttemptedSubmit, phoneError, emailError])

  const customEditorSubmitRef = useRef<() => Promise<Link[]>>()

  const submit = useCallback(async () => {
    const validate = (): boolean => {
      let hasEmailError = false
      let hasPhoneError = false

      if (!isValidEmailOrEmpty(email)) {
        console.log('Email is invalid: ', email)
        setEmailError('Invalid email format')
        hasEmailError = true
      } else {
        console.log('Email is valid: ', email)
      }

      if (!isValidPhoneNumberOrEmpty(phone)) {
        console.log('Phone is invalid: ', phone)
        setPhoneError('Invalid phone number format')
        hasPhoneError = true
      } else {
        console.log('Phone is valid: ', phone)
      }

      return !(hasEmailError || hasPhoneError)
    }

    const addCustomEditorEntityLinks = async (updatedLinks: Link[]) => {
      const customEditorEntityLinks = await customEditorSubmitRef.current?.()
      if (customEditorEntityLinks) {
        updatedLinks.push(...customEditorEntityLinks)
      }
    }

    const getFieldValues = (): DraftFieldValue[] =>
      entity
        ? [...Array.from(fields.values())].filter((field) => {
            const originalValue = entity.entity.valuesByFieldId.asJsReadonlyMapView().get(field.fieldId)
            return !originalValue || fieldValueToString(originalValue) !== field.value
          })
        : [...Array.from(fields.values())]

    const createEntity = async (
      localEntityType: EntityType,
      fieldValues: readonly DraftFieldValue[],
      updatedLinks: readonly Link[],
      externalValues: Map<ExternalValueKey, Nullable<string>>
    ) => {
      try {
        await entityRepository.createEntity(
          createEntityToCreate(entityType.id, fieldValues, updatedLinks, externalValues || new Map())
        )
        showSnackBar(`Created ${getTypeDisplayName(localEntityType.name)}`, 'success')
        navigate(-1)
      } catch (e) {
        logException(e)
        if (e instanceof ConflictError) {
          showSnackBar(`Entity login identifier already exists. Please check email and phone number.`, 'error')
        } else {
          showSnackBar(`Failed to create ${getTypeDisplayName(localEntityType.name)}`, 'error')
        }
      }
    }

    const modifyEntity = async (
      entity: EntityWithLabelAndRelated,
      localEntityType: EntityType,
      fieldValues: readonly DraftFieldValue[],
      updatedLinks: readonly Link[],
      externalValues: Map<ExternalValueKey, Nullable<string>>
    ) => {
      try {
        await entityRepository.modifyEntity(entity.entity.id, {
          valuesToUpdateByFieldId: draftFieldValuesToFieldValues(fieldValues),
          linksToUpdate: new Map(updatedLinks.map((link) => [link.key, link.value])),
          externalValuesToUpdate: externalValues,
        })
        showSnackBar(`${getTypeDisplayName(localEntityType.name)} updated`, 'success')
        navigate(-1)
      } catch (e) {
        logException(e)
        if (e instanceof ConflictError) {
          showSnackBar(`Entity login identifier already exists. Please check email and phone number.`, 'error')
        } else {
          showSnackBar(`Failed to update ${getTypeDisplayName(localEntityType.name)}`, 'error')
        }
      }
    }

    setHasAttemptedSubmit(true)

    if (!validate()) return

    setSubmitting(true)
    try {
      const updatedLinks: Link[] = []
      const externalValues = new Map<ExternalValueKey, Nullable<string>>()

      await addCustomEditorEntityLinks(updatedLinks)

      externalValues.set(phoneNumberLoginIdentifierKey, PhoneNumber.fromString(phone)?.normalized || null)
      externalValues.set(emailLoginIdentifierKey, email || null)

      const localEntityType = requireNotUndefinedOrNull(entityType)
      const fieldValues = getFieldValues()

      if (!entity) {
        await createEntity(localEntityType, fieldValues, updatedLinks, externalValues)
      } else {
        await modifyEntity(entity, localEntityType, fieldValues, updatedLinks, externalValues)
      }
    } finally {
      setSubmitting(false)
    }
  }, [
    entityRepository,
    navigate,
    entityType,
    entity,
    fields,
    showSnackBar,
    phone,
    email,
    phoneNumberLoginIdentifierKey,
    emailLoginIdentifierKey,
    setEmailError,
    setPhoneError,
  ])

  const confirmDelete = async () => {
    const localEntityType = requireNotUndefinedOrNull(entityType)
    try {
      await entityRepository.deleteEntity(requireNotUndefinedOrNull(entityId))
      showSnackBar(`Deleted ${getTypeDisplayName(localEntityType.name)}`, 'success')
      navigate(`/entities/${localEntityType.id.toString()}`)
    } catch (e) {
      logException(e)
      showSnackBar(`Failed to delete ${getTypeDisplayName(localEntityType.name)}`, 'error')
    }
  }

  const fieldsSection =
    entityType && hasRequiredEntityActions ? (
      <Stack spacing={3}>
        <Stack padding={0} spacing={3}>
          {entityType.fields
            .asJsReadonlyArrayView()
            .filter(
              (field) =>
                !isManualOrderField(field) &&
                !(isRenderableAssetType && payloadField && field.id.equals(payloadField.id))
            )
            .map((field) => (
              <EntityFormInput
                key={`entity-form-field-${field.id.toString()}`}
                field={field}
                fieldValue={fields.get(field.id)}
                entityTypes={entityTypes}
                updateField={updateField}
              />
            ))}
        </Stack>
        {entityType.kind === EntityTypeKind.User && (
          <LoginIdentifierField
            phone={phone}
            setPhone={setPhone}
            email={email}
            setEmail={setEmail}
            phoneError={phoneError}
            emailError={emailError}
          />
        )}
        {entityType.kind === EntityTypeKind.LearningUnit && entity && (
          <LearningUnitEditorWrapper
            entityId={entity.entity.id.toString()}
            entityLinks={[...(entity.entity.links.asJsReadonlyMapView().entries() || [])].map(([key, value]) => ({
              key,
              value,
            }))}
            linkKeyFromString={linkKeyFromString}
            onRegisterPartialSubmit={(fn: () => Promise<Link[]>) => {
              customEditorSubmitRef.current = fn
            }}
            mode="edit"
          />
        )}
      </Stack>
    ) : null

  const isAssetEditor = Boolean(fieldsSection && isRenderableAssetType)
  const headerSection = (
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="h4">{getTypeDisplayName(entityType.name)}</Typography>
      {entity && (
        <ConfirmationDialog
          title={`There may still be references to this ${
            entityType ? getTypeDisplayName(entityType.name) : 'entity'
          } in the system. Are you sure you want to
                      delete this ${entityType ? getTypeDisplayName(entityType.name) : 'entity'}? This action cannot be undone.`}
          content={
            <Stack direction="row" alignItems="center" spacing={1}>
              <DeleteOutlineIcon fontSize="small" />
              <Typography fontWeight={600}>DELETE</Typography>
            </Stack>
          }
          onConfirm={confirmDelete}
        />
      )}
    </Stack>
  )
  const actionsSection = (
    <Stack direction="row" spacing={3} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
      <Button onClick={() => navigate(-1)}>Cancel</Button>
      <Button variant="contained" onClick={submit} disabled={!valid}>
        Save
      </Button>
    </Stack>
  )
  const editorContent = isAssetEditor ? (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      <AssetPage
        fieldsSection={fieldsSection}
        headerSection={headerSection}
        payloadEnabled={!!payloadField}
        payloadValue={assetPayload}
        onPayloadChange={payloadField ? handleAssetPayloadChange : undefined}
        assetType={activeAssetType}
        renderData={renderData}
        previewUpdateToken={assistantUpdateToken}
        actionsSection={actionsSection}
      />
    </Box>
  ) : (
    fieldsSection
  )

  const loadingOverlayOpen = submitting || loading || !(error || entityType)

  return (
    <Stack padding={3} spacing={3} sx={isAssetEditor ? { height: '100%', minHeight: 0 } : undefined}>
      {entityType && hasRequiredEntityActions && (
        <>
          {!isAssetEditor && headerSection}
          {editorContent}
          {!isAssetEditor && actionsSection}
        </>
      )}
      {!hasRequiredEntityActions && !loadingOverlayOpen && (
        <PermissionError missingPermittedActions={missingEntityActions} />
      )}
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </Stack>
  )
}

const CreateOrEditPage = () => {
  const params = useParams()
  const entityId = params.entityId?.let(entityIdFromString)
  const entityTypeId = entityTypeIdFromString(requireNotUndefinedOrNull(params.entityTypeId))

  const entityTypeRepository = useContext(EntityTypesContext)
  const entityTypes = entityTypeRepository.useAll()
  const entityType = entityTypeRepository.useItem(entityTypeId)
  const error = entityTypeRepository.useError()?.let((it) => String(it))

  const loadingType = entityTypeRepository.useLoading()
  const loadingOverlayOpen = loadingType && !error

  return (
    <>
      {entityType && entityTypes && (
        <ReadyCreateOrEditPage entityId={entityId} entityType={entityType} entityTypes={entityTypes} />
      )}
      {(!entityType || !entityTypes) && !loadingOverlayOpen && <EntityTypeNotAccessibleError />}
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </>
  )
}

export default CreateOrEditPage
